import {
  type ActionEventArgs,
  type EventClickArgs,
  type EventRenderedArgs,
  type PopupOpenEventArgs,
  type ScheduleComponent,
} from '@syncfusion/ej2-react-schedule'
import dayjs from 'dayjs'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import type { AppointmentCategories } from 'features/Schedule/api/schedule.types'
import type { AppointmentColorType } from 'features/SettingsOld/interfaces/ISettings'
import type { StatusColorName } from 'features/SettingsOld/state/api/types'
import { toAppointmentSeriesLink } from 'features/Series/api/series.transform'
import { applySeriesSelection } from 'features/Series/calendar/applySeriesSelection'
import {
  clearSeriesSelection,
  clearSeriesSelectionIfGone,
  getSeriesSelection,
  selectSeries,
  subscribeSeriesSelection,
} from 'features/Series/calendar/seriesSelectionStore'
import { SERIES_PILL_ATTR } from 'features/Series/components/SeriesBadge'
import { SHOW_SERIES } from 'features/Series/series.flags'
import {
  type MutableRefObject,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { type AppointmentColors, applyAppointmentColors } from './applyAppointmentColors'
import { applyStaffColorToken, applyStaffFocus } from './applyStaffFocus'
import CalendarEvent from './CalendarEvent'
import {
  applyAppointmentFill,
  applyScheduleColumnWidths,
  APPOINTMENT_INITIALS_LEFT_PX,
  APPOINTMENT_INITIALS_WIDTH_INSET_PX,
  getAppointmentColor,
  getAppointmentColorInputFromEvent,
  getMaxConcurrentByColumnKey,
} from './helpers'
import { revertScheduleDomChange } from './safeRefreshScheduleEvents'
import { scheduleEventStore } from './scheduleEventStore'
import { useScheduleColumnResize } from './useScheduleColumnResize'
import { useSilentEventSync } from './useSilentEventSync'

type ScheduleEventData = IPatientSchedule & {
  status?: { code?: number }
  category?: { code?: number }
}

type UseScheduleViewParams = {
  data: IPatientSchedule[]
  schedules: IPatientSchedule[]
  scheduleObj: RefObject<ScheduleComponent | null>
  interval: number
  dayInterval: { min: string; max: string }
  day?: string
  timeZone?: string
  appointmentColor: AppointmentColorType
  appointmentCategories: AppointmentCategories[]
  patientStatuses: StatusColorName[]
  hasEhrBooking: boolean
  showConsultationStaffInitials: boolean
  isWeekView?: boolean
  columnKeys?: string[]
  flexColumnKey?: string
  /** Per-column unit widths at default-zoom reference scale (user localStorage prefs). */
  referenceWidthByColumn?: Readonly<Record<string, number>>
  setColumnReferenceWidth?: (columnKey: string, referenceWidthPx: number) => void
  setEditedPatient: (patient: IPatientSchedule | null) => void
  prepareScheduleChange: (data: IPatientSchedule) => unknown | null
  commitScheduleChange: (prepared: any, event: IPatientSchedule) => Promise<void>
  skipNextEventChangeRef?: MutableRefObject<boolean>
  instanceKey?: string
  focusedStaffIds?: number[]
  scaleColumnsByOverlap?: boolean
}

const findEventById = (
  events: IPatientSchedule[],
  id: number | string | undefined,
): IPatientSchedule | undefined => {
  if (id == null) return undefined
  return events.find((event) => event.id === id || Number(event.id) === Number(id))
}

const toLocalCalendarDate = (dateKey?: string): Date => {
  if (!dateKey) return new Date()
  const [year, month, dayOfMonth] = dateKey.split('-').map(Number)
  if (!year || !month || !dayOfMonth) return new Date(dateKey)
  return new Date(year, month - 1, dayOfMonth, 0, 0, 0, 0)
}

/**
 * Shared Syncfusion scheduler wiring used by both the day and week calendars:
 * appointment colouring, the event template, and the common event handlers/config.
 * View-specific concerns (resources, render-cell rules, selection) stay in each view.
 */
export const useScheduleView = ({
  data,
  schedules,
  scheduleObj,
  interval,
  dayInterval,
  day,
  timeZone,
  appointmentColor,
  appointmentCategories,
  patientStatuses,
  hasEhrBooking,
  showConsultationStaffInitials,
  isWeekView = false,
  columnKeys = [],
  flexColumnKey,
  referenceWidthByColumn = {},
  setColumnReferenceWidth,
  setEditedPatient,
  prepareScheduleChange,
  commitScheduleChange,
  skipNextEventChangeRef,
  instanceKey,
  focusedStaffIds,
  scaleColumnsByOverlap = true,
}: UseScheduleViewParams) => {
  const dataRef = useRef(data)
  const schedulesRef = useRef(schedules)
  const columnKeysRef = useRef(columnKeys)
  const flexColumnKeyRef = useRef(flexColumnKey)
  const referenceWidthByColumnRef = useRef(referenceWidthByColumn)
  const focusedStaffIdsRef = useRef(focusedStaffIds)
  const scaleColumnsByOverlapRef = useRef(scaleColumnsByOverlap)
  const showConsultationStaffInitialsRef = useRef(showConsultationStaffInitials)
  const scheduleInstanceKey = instanceKey ?? `${isWeekView ? 'week' : 'day'}:${day ?? ''}`

  useEffect(() => {
    dataRef.current = data
  }, [data])

  useEffect(() => {
    schedulesRef.current = schedules
  }, [schedules])

  useEffect(() => {
    columnKeysRef.current = columnKeys
  }, [columnKeys])

  useEffect(() => {
    flexColumnKeyRef.current = flexColumnKey
  }, [flexColumnKey])

  useEffect(() => {
    referenceWidthByColumnRef.current = referenceWidthByColumn
  }, [referenceWidthByColumn])

  useEffect(() => {
    focusedStaffIdsRef.current = focusedStaffIds
  }, [focusedStaffIds])

  useEffect(() => {
    scaleColumnsByOverlapRef.current = scaleColumnsByOverlap
  }, [scaleColumnsByOverlap])

  useEffect(() => {
    showConsultationStaffInitialsRef.current = showConsultationStaffInitials
  }, [showConsultationStaffInitials])

  const handleColumnResizeEnd = useCallback(
    (columnKey: string, referenceWidthPx: number) => {
      setColumnReferenceWidth?.(columnKey, referenceWidthPx)
    },
    [setColumnReferenceWidth],
  )

  const { reattachColumnResizeHandles } = useScheduleColumnResize({
    scheduleObj,
    onResizeEnd: handleColumnResizeEnd,
  })

  const applyDynamicColumnWidths = useCallback(() => {
    const schedule = scheduleObj.current
    if (!schedule?.element) return

    const root = schedule as unknown as { isDestroyed?: boolean }
    if (root.isDestroyed) return

    let keys = columnKeysRef.current
    if (isWeekView) {
      keys = schedule.getCurrentViewDates().map((date) => String(dayjs(date).day()))
    }

    const maxConcurrentByColumn = getMaxConcurrentByColumnKey(dataRef.current)
    const overlapByColumn = scaleColumnsByOverlapRef.current
      ? maxConcurrentByColumn
      : new Map<string, number>()
    const fillSingleDayColumn = !isWeekView && keys.length === 1

    if (keys.length > 0) {
      applyScheduleColumnWidths({
        scheduleElement: schedule.element,
        columnKeys: keys,
        maxConcurrentByColumn: overlapByColumn,
        interval,
        referenceWidthByColumn: referenceWidthByColumnRef.current,
        fillContainer: fillSingleDayColumn,
        flexColumnKey: isWeekView ? undefined : flexColumnKeyRef.current,
        scaleColumnsByOverlap: scaleColumnsByOverlapRef.current,
      })
    }

    const hasInitials = showConsultationStaffInitialsRef.current
    applyAppointmentFill(schedule.element, {
      leftGutterPx: hasInitials ? APPOINTMENT_INITIALS_LEFT_PX : 0,
      widthInsetPx: hasInitials ? APPOINTMENT_INITIALS_WIDTH_INSET_PX : 0,
    })
    reattachColumnResizeHandles({
      columnKeys: fillSingleDayColumn ? [] : keys,
      interval,
      maxConcurrentByColumn: overlapByColumn,
      isWeekView,
    })
  }, [scheduleObj, isWeekView, interval, reattachColumnResizeHandles, scaleColumnsByOverlap])

  const getEventColors = useCallback(
    (eventData: IPatientSchedule): AppointmentColors => {
      const live =
        findEventById(dataRef.current, eventData.id) ??
        findEventById(schedulesRef.current, eventData.id) ??
        eventData

      const { statusCode, categoryCode } = getAppointmentColorInputFromEvent(
        live,
        schedulesRef.current,
      )
      return getAppointmentColor({
        appointmentColor,
        categoryStatuses: appointmentCategories,
        statusColorName: patientStatuses,
        statusCode,
        categoryCode,
      })
    },
    [appointmentColor, appointmentCategories, patientStatuses],
  )

  const getEventColorsRef = useRef(getEventColors)
  getEventColorsRef.current = getEventColors

  const applyEventChrome = useCallback((element: HTMLElement, event: IPatientSchedule) => {
    const colors = getEventColorsRef.current(event)
    applyAppointmentColors(element, colors)
    element.style.setProperty('--schedule-pill-border', colors.borderColor ?? 'var(--light-03)')
    applySeriesSelection(element, event, getSeriesSelection())
    if (focusedStaffIdsRef.current === undefined) return
    applyStaffColorToken(element, event)
    applyStaffFocus(
      element,
      event,
      focusedStaffIdsRef.current,
      isWeekView ? undefined : flexColumnKeyRef.current,
    )
    if (focusedStaffIdsRef.current.length) {
      element.style.setProperty('--schedule-focus-border', colors.borderColor ?? '')
    }
  }, [])

  const reapplyAppointmentColors = useCallback(() => {
    const schedule = scheduleObj.current
    if (!schedule?.element) return

    const root = schedule as unknown as { isDestroyed?: boolean }
    if (root.isDestroyed) return

    for (const event of dataRef.current) {
      schedule.element
        .querySelectorAll<HTMLElement>(
          `[data-id="Appointment_${event.id}"]:not(.e-schedule-event-clone)`,
        )
        .forEach((element) => applyEventChrome(element, event))
    }
  }, [applyEventChrome, scheduleObj])

  const eventRendered = useCallback(
    (props: EventRenderedArgs) => {
      applyEventChrome(props.element, props.data as IPatientSchedule)
    },
    [applyEventChrome],
  )

  useEffect(() => {
    reapplyAppointmentColors()
  }, [focusedStaffIds, reapplyAppointmentColors])

  // A Series selection outlines and dims cards; leaving the calendar drops it.
  useEffect(() => {
    const unsubscribe = subscribeSeriesSelection(reapplyAppointmentColors)
    return () => {
      unsubscribe()
      clearSeriesSelection()
    }
  }, [reapplyAppointmentColors])

  const dropGoneSeriesSelection = useCallback(() => {
    const element = scheduleObj.current?.element
    if (element) clearSeriesSelectionIfGone(element.ownerDocument)
  }, [scheduleObj])

  const applyDynamicColumnWidthsRef = useRef(applyDynamicColumnWidths)
  applyDynamicColumnWidthsRef.current = applyDynamicColumnWidths
  const reapplyAppointmentColorsRef = useRef(reapplyAppointmentColors)
  reapplyAppointmentColorsRef.current = reapplyAppointmentColors
  const dropGoneSeriesSelectionRef = useRef(dropGoneSeriesSelection)
  dropGoneSeriesSelectionRef.current = dropGoneSeriesSelection
  const setEditedPatientRef = useRef(setEditedPatient)
  setEditedPatientRef.current = setEditedPatient

  const instanceKeyRef = useRef(scheduleInstanceKey)
  instanceKeyRef.current = scheduleInstanceKey

  // Opens the dialog with the appointment as it is now; stable so templates do not re-render.
  const openAppointment = useCallback((event: IPatientSchedule) => {
    const current = scheduleEventStore.get(Number(event.id), instanceKeyRef.current) as
      | IPatientSchedule
      | undefined
    clearSeriesSelection()
    setEditedPatientRef.current(current ?? event)
  }, [])

  const dataSource = useSilentEventSync({
    scheduleObj,
    data,
    getEventColors,
    timeZone,
    instanceKey: scheduleInstanceKey,
    onPatched: () => {
      applyDynamicColumnWidthsRef.current()
      reapplyAppointmentColorsRef.current()
      dropGoneSeriesSelectionRef.current()
    },
  })

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      applyDynamicColumnWidths()
    })
    return () => cancelAnimationFrame(frameId)
  }, [columnKeys, referenceWidthByColumn, applyDynamicColumnWidths])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 576px)')
    const handleBreakpointChange = () => applyDynamicColumnWidths()
    mediaQuery.addEventListener('change', handleBreakpointChange)
    window.addEventListener('resize', handleBreakpointChange)
    return () => {
      mediaQuery.removeEventListener('change', handleBreakpointChange)
      window.removeEventListener('resize', handleBreakpointChange)
    }
  }, [applyDynamicColumnWidths])

  const eventTemplate = useCallback(
    (props: ScheduleEventData) => (
      <CalendarEvent
        event={props}
        appointmentColor={appointmentColor}
        appointmentCategories={appointmentCategories}
        patientStatuses={patientStatuses}
        isWeekView={isWeekView}
        hasEhrBooking={hasEhrBooking}
        showConsultationStaffInitials={showConsultationStaffInitials}
        timeZone={timeZone}
        instanceKey={scheduleInstanceKey}
        openAppointment={openAppointment}
      />
    ),
    [
      appointmentColor,
      appointmentCategories,
      openAppointment,
      hasEhrBooking,
      scheduleInstanceKey,
      isWeekView,
      patientStatuses,
      showConsultationStaffInitials,
      timeZone,
    ],
  )

  const handleEventClick = useCallback(
    (props: EventClickArgs) => {
      const originalEvent = (props as { originalEvent?: Event }).originalEvent
      originalEvent?.preventDefault()
      originalEvent?.stopPropagation()
      const clicked = props.event as IPatientSchedule
      const event =
        (scheduleEventStore.get(Number(clicked.id), scheduleInstanceKey) as
          | IPatientSchedule
          | undefined) ?? clicked
      // Only the "k/N" pill of a Series card opens its popover; the rest of the card opens the visit.
      const link = SHOW_SERIES ? toAppointmentSeriesLink(event.series) : null
      const onPill = (originalEvent?.target as Element | null)?.closest?.(`[${SERIES_PILL_ATTR}]`)
      if (link && onPill) {
        selectSeries({ seriesId: link.id, appointmentId: Number(event.id), event })
        return
      }
      openAppointment(event)
    },
    [openAppointment, scheduleInstanceKey],
  )

  // Drag and resize of any card drop the Series selection.
  const handleEventInteractionStart = useCallback(() => clearSeriesSelection(), [])

  const handleRemoveSelectedCells = useCallback(() => {
    document.querySelectorAll('.e-selected-cell')?.forEach((el) => {
      el.classList.remove('e-selected-cell')
    })
  }, [])

  const handleChange = useCallback(
    async (props: ActionEventArgs) => {
      if (props.requestType !== 'eventChange') {
        return
      }

      if (skipNextEventChangeRef?.current) {
        skipNextEventChangeRef.current = false
        return
      }

      const changedEvent = props.data as IPatientSchedule

      const prepared = prepareScheduleChange(changedEvent)

      if (!prepared) {
        props.cancel = true
        handleRemoveSelectedCells()
        requestAnimationFrame(() => {
          revertScheduleDomChange(scheduleObj.current)
          reapplyAppointmentColors()
        })
        return
      }

      commitScheduleChange(prepared, changedEvent).finally(() => {
        handleRemoveSelectedCells()
        requestAnimationFrame(() => reapplyAppointmentColors())
      })
    },
    [
      prepareScheduleChange,
      commitScheduleChange,
      handleRemoveSelectedCells,
      reapplyAppointmentColors,
      scheduleObj,
      skipNextEventChangeRef,
    ],
  )

  const handleDataBound = useCallback(() => {
    scheduleObj.current?.hideSpinner()
    reapplyAppointmentColors()
    applyDynamicColumnWidths()
    dropGoneSeriesSelection()
  }, [scheduleObj, reapplyAppointmentColors, applyDynamicColumnWidths, dropGoneSeriesSelection])

  const handleActionFailure = useCallback(() => {
    scheduleObj.current?.hideSpinner()
  }, [scheduleObj])

  const handlePopupOpen = useCallback((props: PopupOpenEventArgs) => {
    if (props.type === 'Editor') {
      props.cancel = true
    }
  }, [])

  const majorSlotTemplate = useCallback(
    (props: { date: Date }) => (
      <div className='e-time-slot-wrap'>{dayjs(props.date).format('h:mm A')}</div>
    ),
    [],
  )

  const selectedDate = useMemo(() => {
    return toLocalCalendarDate(day)
  }, [day])

  const timeScale = useMemo(
    () => ({ interval, slotCount: 1, majorSlotTemplate }),
    [interval, majorSlotTemplate],
  )

  const workHours = useMemo(
    () => ({ highlight: true, start: dayInterval.min, end: dayInterval.max }),
    [dayInterval.min, dayInterval.max],
  )

  const eventSettings = useMemo(
    () => ({
      dataSource,
      enableTooltip: false,
      enableIndicator: true,
      fields: {
        id: 'id',
        startTime: { title: 'From', name: 'start_time' },
        endTime: { title: 'To', name: 'end_time' },
      },
    }),
    [dataSource],
  )

  return {
    eventRendered,
    eventTemplate,
    handleEventClick,
    handleEventInteractionStart,
    handleChange,
    handleDataBound,
    handleActionFailure,
    handlePopupOpen,
    handleRemoveSelectedCells,
    reapplyAppointmentColors,
    applyDynamicColumnWidths,
    majorSlotTemplate,
    selectedDate,
    timeScale,
    workHours,
    eventSettings,
  }
}
